import React, { useRef, useState } from 'react';
import { MdDownload, MdVerifiedUser } from 'react-icons/md';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import ApplicationPDFContent from '../components/ApplicationPDFContent';

const FinalSubmissionPage = ({ handleBack, handleSubmit, formData, studentId, applicationId, registerStudent }) => {
    const [emailSent, setEmailSent] = useState(false);
    const previewRef = useRef(null);

    const [isDownloading, setIsDownloading] = useState(false);

    const handleDownloadClick = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setIsDownloading(true);

        try {


            if (!previewRef.current) {
                console.error("Preview element not found");
                setIsDownloading(false);
                return;
            }
            setIsDownloading(true);

            console.log("Starting PDF generation...");
            const element = previewRef.current;
            if (!element) {
                console.error("Preview element not found");
                return;
            }

            // Ensure fonts are ready, then explicitly force-load Arabic font
            // (Safari doesn't auto-load fonts for off-screen/hidden content)
            await document.fonts.ready;
            await Promise.all([
                document.fonts.load('400 16px "Noto Sans Arabic"', 'أبجد'),
                document.fonts.load('700 16px "Noto Sans Arabic"', 'أبجد'),
            ]).catch(() => {});
            console.log("Fonts ready, waiting for layout stability...");

            // Increased delay for 11 pages to fully render images/fonts
            await new Promise(resolve => setTimeout(resolve, 3000));

            const pages = element.querySelectorAll('.page');
            console.log(`Found ${pages.length} pages to process`);
            
            if (pages.length === 0) {
                console.error("No elements with class '.page' found inside previewRef");
                alert("Generation error: No pages found. Please wait a moment and try again.");
                return;
            }

            const pdf = new jsPDF('portrait', 'mm', 'a4');
            const pageWidth = 210;
            const pageHeight = 297;

            for (let i = 0; i < pages.length; i++) {
                console.log(`Processing page ${i + 1}/${pages.length}...`);
                try {
                    const canvas = await html2canvas(pages[i], {
                        scale: 2, // Reduced from 3 to 2 for memory stability on large docs
                        useCORS: true,
                        allowTaint: false,
                        backgroundColor: '#ffffff',
                        logging: true, // Enable logging to see progress in console
                        windowWidth: 794,
                        onclone: (clonedDoc) => {
                            // Copy all loaded FontFace objects to the cloned document.
                            // This is required for Safari — @import in onclone is async
                            // and html2canvas won't wait for it, causing Arabic text to
                            // fall back to a font that doesn't support the script.
                            document.fonts.forEach(fontFace => {
                                if (fontFace.status === 'loaded') {
                                    try { clonedDoc.fonts.add(fontFace); } catch (_) {}
                                }
                            });
                            const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                            const style = clonedDoc.createElement('style');
                            style.textContent = `
                                [dir="rtl"], [dir="rtl"] * {
                                    font-family: 'Noto Sans Arabic', 'Geeza Pro', 'Segoe UI', Tahoma, Arial, sans-serif !important;
                                    direction: rtl !important;
                                    letter-spacing: normal !important;
                                    word-spacing: normal !important;
                                    font-weight: normal !important;
                                    font-style: normal !important;
                                    ${isSafari ? 'text-align: right !important;' : ''}
                                }
                            `;
                            clonedDoc.head.appendChild(style);
                        }
                    });

                    const imgData = canvas.toDataURL('image/jpeg', 0.95); // Use JPEG for smaller size
                    
                    if (i > 0) {
                        pdf.addPage();
                    }

                    pdf.addImage(imgData, 'JPEG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');
                    console.log(`Page ${i + 1} added to PDF`);
                } catch (pageErr) {
                    console.error(`Error processing page ${i + 1}:`, pageErr);
                }
            }

            const fileName = `AUMS_Application_${formData?.civilId?.value || 'Form'}.pdf`;
            pdf.save(fileName);
            console.log("PDF saved successfully");
        } catch (error) {
            console.error("Fatal error during PDF generation:", error);
            alert("An error occurred during PDF generation. Please check the console for details.");
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <>
            <div className="no-print bg-white min-h-screen py-12 px-6">
                <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-12 bg-[#b60041] p-4 rounded-md shadow-lg">
                    <h1 className="text-white text-lg md:text-xl lg:text-2xl font-bold text-center uppercase tracking-tight whitespace-nowrap">
                        TWO STEPS TO SUBMIT YOUR FINAL APPLICATION : Step A & Step B
                    </h1>
                </div>

                {/* STEP A */}
                <div className="mb-12">
                    <h2 className="text-lg font-bold underline mb-6">STEP A:</h2>

                    {/* Download Section */}
                    <div className="mb-6">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="font-semibold">1. Download the complete application form</span>
                             <button
                                type="button"
                                onClick={(e) => handleDownloadClick(e)}
                                disabled={isDownloading}
                                className={`inline-flex items-center gap-2 text-white px-6 py-2 rounded font-bold transition-all ${
                                    isDownloading ? 'bg-gray-400 cursor-wait' : 'bg-red-600 hover:bg-red-700'
                                }`}
                            >
                                <MdDownload size={18} />
                                {isDownloading ? 'GENERATING...' : 'DOWNLOAD'}
                            </button>
                        </div>

                        {/* Numbered List */}
                        <div className="space-y-2 ml-6 text-gray-800">
                            <p><span className="font-semibold">2.</span> Print it</p>
                            <p><span className="font-semibold">3.</span> Sign it</p>
                            <p><span className="font-semibold">4.</span> Scan the document after signing</p>
                            <p><span className="font-semibold">5.</span> Make it into a single PDF file</p>
                            <p>
                                <span className="font-semibold">6.</span> Send the PDF file to email:{' '}
                                <a href="mailto:Admission1@aums.edu.kw" className="text-red-600 font-bold hover:underline">
                                    Admission1@aums.edu.kw
                                </a>
                            </p>
                            <p><span className="font-semibold">7.</span> Keep the original signed papers to submit to Admissions Office upon request</p>
                        </div>
                    </div>

                    {/* Important Note */}
                    <div className="mb-6">
                        <h3 className="font-bold text-gray-800 mb-2">Important note:</h3>
                        <p className="text-gray-800">
                            Your application <span className="underline text-red-600 font-bold">will not be valid</span> until all the above instructions have been completed.
                        </p>
                    </div>

                    {/* Checkbox with Yellow Background */}
                    <div className="bg-yellow-300 p-4 rounded border-2 border-yellow-400 mb-6">
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={emailSent}
                                onChange={(e) => setEmailSent(e.target.checked)}
                                className="mt-1 w-5 h-5 cursor-pointer accent-yellow-600"
                            />
                            <span className="font-semibold text-gray-800">
                                I have downloaded my application form and have sent it to email:{' '}
                                <a href="mailto:Admission1@aums.edu.kw" className="text-red-600 font-bold hover:underline">
                                    Admission1@aums.edu.kw
                                </a>
                            </span>
                        </label>
                    </div>
                </div>

                {/* Divider & STEP B - Only visible when checked */}
                {emailSent && (
                    <>
                        <div className="border-t-2 border-gray-300 my-8"></div>
                        <div className="mb-12 animate-in fade-in slide-in-from-top-4 duration-500">
                            <h2 className="text-lg font-bold underline mb-6">STEP B:</h2>
                            <p className="text-gray-800 mb-6">
                                CLICK <span className="font-bold text-red-600">(SUBMIT)</span> BUTTON BELOW TO SUBMIT YOUR APPLICATION ELECTRONICALLY
                            </p>
                        </div>
                    </>
                )}

                {/* Buttons */}
                <div className="flex gap-4 justify-end border-t-2 border-gray-300 pt-8">
                    <button
                        type="button"
                        onClick={handleBack}
                        className="border-2 border-gray-400 text-gray-800 px-8 py-2 rounded font-bold hover:bg-gray-100 transition-all"
                    >
                        BACK
                    </button>
                     {emailSent && (
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className="bg-green-500 text-white px-8 py-2 rounded font-bold hover:bg-green-600 transition-all flex items-center gap-2 animate-in fade-in zoom-in duration-300"
                        >
                            SUBMIT
                        </button>
                    )}
                </div>
            </div>
            </div>

            <div style={{ 
                position: 'fixed', 
                left: '-20000px', 
                top: 0, 
                width: '210mm', 
                zIndex: -9999,
                opacity: 1,
                pointerEvents: 'none'
            }}>
                <div ref={previewRef}>
                    <ApplicationPDFContent formData={formData} studentId={studentId} applicationId={applicationId} />
                </div>
            </div>
        </>
    );
};

export default FinalSubmissionPage;
